import { Button } from '@heidi/design-system/button';
import { HeidiLogoMark } from '@heidi/design-system/heidi-logo-mark';
import {
  TypographyCaption,
  TypographyH3,
  TypographyP2,
  TypographyP2Bold,
} from '@heidi/design-system/typography';
import { ChevronRight, LogOut } from 'lucide-react';
import { useIntl } from 'react-intl';

import {
  type JoinedWorkspaceRelation,
  type WorkspaceInvitationView,
} from '../../contracts';
import { sortWorkspacesByName } from '../../data/query/useHydrateWorkspaceId';
import { WorkspaceAvatar } from '../workspace/workspace-avatar';
import { workspaceSelectorMessages } from './workspace-selector.messages';

export interface WorkspaceSelectorProps {
  /** The workspaces the signed-in user belongs to; rendered alphabetically. */
  workspaces: JoinedWorkspaceRelation[];
  onSelect: (workspaceId: string) => void;
  /** Pending invitations, listed under the workspaces with a "Join team" action. */
  invitations?: WorkspaceInvitationView[];
  onJoin?: (invitation: WorkspaceInvitationView) => void;
  /**
   * The invitation being joined: `joining` disables its button and reads
   * "Joining…"; `failed` shows an inline error under it.
   */
  joinState?: InvitationJoinState | null;
  /**
   * Renders a Sign out utility when provided: a top-left corner button from
   * `md:` (768px) up, a bottom sticky bar below that.
   */
  onSignOut?: () => void;
}

interface InvitationJoinState {
  invitationId: string;
  status: 'joining' | 'failed';
}

/**
 * The full-screen "Where to today?" surface a multi-workspace user sees after
 * signing in, before the app mounts. One button per workspace, plus an
 * opt-in Sign out utility: top-left from `md:` up, a bottom sticky bar on
 * phones below that.
 *
 * The utility only renders when `onSignOut` is supplied, so selecting a
 * workspace stays the only way off this surface until a caller opts in. It
 * names nobody: who is signed in cannot be fed to this surface.
 *
 * It decides nothing. WHEN it shows comes from `useWorkspaceHydrationStatus`
 * (`@heidi/workspace/data`); what selecting (and signing out) does stays
 * app-side in `WorkspaceHydrationGate`, which is what lets it mount above
 * `UserResolver` with only an `IntlProvider` above it — the same contract as
 * `WorkspaceHydrationError`.
 *
 * Rows come from `sortWorkspacesByName`, the same ordering
 * `pickDefaultWorkspaceId` takes its head from, so the workspace the app would
 * have picked on its own is always the first row.
 */
export const WorkspaceSelector = ({
  workspaces,
  onSelect,
  invitations = [],
  onJoin,
  joinState = null,
  onSignOut,
}: WorkspaceSelectorProps) => {
  const intl = useIntl();
  const sortedWorkspaces = sortWorkspacesByName(workspaces);
  const showInvitations = invitations.length > 0 && Boolean(onJoin);

  return (
    <div
      className="flex h-dvh w-full flex-col overflow-hidden bg-surface-page"
      data-testid="workspace-selector"
    >
      {/* `min-h-0` on both this wrapper and `main` overrides the flex default
          of sizing a flex item to its content, which is what lets `main`
          actually shrink and scroll internally instead of growing past the
          fixed-height shell above — `body` has `overflow: hidden` app-wide, so
          with no internal scroll region a tall list is simply unreachable. */}
      <div className="relative flex min-h-0 flex-1 flex-col">
        <main className="flex min-h-0 w-full flex-1 flex-col items-center overflow-y-auto px-6 py-12">
          {/* `md:my-auto` centers vertically from `md:` up — but as an AUTO
              MARGIN, not `justify-center`. `justify-center` on an overflowing
              scroll container centers the oversized content on both axes,
              which pushes its start (the heading) above `scrollTop: 0` with
              no way to scroll up to it — unreachable, not just off-screen.
              An auto margin has no such trap: the browser can only ever
              collapse it to 0, so a list long enough to overflow just
              degrades to top-aligned and fully reachable by scrolling,
              whatever its length turns out to be. */}
          <div className="flex w-full max-w-xl flex-col gap-8 md:my-auto">
            <div className="flex flex-col gap-2">
              <HeidiLogoMark
                artwork="standard"
                className="mb-4 size-10"
                data-testid="workspace-selector-mark"
              />
              <TypographyH3>
                {intl.formatMessage(workspaceSelectorMessages.heading)}
              </TypographyH3>
              <TypographyP2 className="text-text-secondary">
                {intl.formatMessage(workspaceSelectorMessages.subheading)}
              </TypographyP2>
            </div>

            <ul
              className="flex flex-col gap-3"
              aria-label={intl.formatMessage(
                // A merged name while invitations are present, so a screen reader
                // doesn't announce an unjoined workspace under "Your workspaces".
                showInvitations
                  ? workspaceSelectorMessages.workspaceAndInvitesListLabel
                  : workspaceSelectorMessages.workspaceListLabel
              )}
            >
              {sortedWorkspaces.map((workspace) => (
                <li key={workspace.workspace_id}>
                  <Button
                    type="button"
                    variant="outline"
                    size="lg"
                    disableAutomaticTypographyClasses
                    className="h-auto w-full justify-start gap-4 rounded-xl p-4 text-start font-normal"
                    onClick={() => onSelect(workspace.workspace_id)}
                    data-testid={`workspace-selector-item-${workspace.workspace_id}`}
                  >
                    <WorkspaceAvatar
                      id={workspace.workspace_id}
                      name={workspace.workspace_name}
                      iconUrl={workspace.icon_url}
                    />
                    <TypographyP2Bold className="min-w-0 flex-1 truncate text-text-primary">
                      {workspace.workspace_name}
                    </TypographyP2Bold>
                    <ChevronRight
                      className="size-5 shrink-0 text-text-secondary rtl:-scale-x-100"
                      aria-hidden
                    />
                  </Button>
                </li>
              ))}

              {invitations.length > 0 &&
                onJoin &&
                invitations.map((invitation) => (
                  <InvitationRow
                    key={invitation.invitation_id}
                    invitation={invitation}
                    onJoin={onJoin}
                    status={
                      joinState?.invitationId === invitation.invitation_id
                        ? joinState.status
                        : null
                    }
                  />
                ))}
            </ul>
          </div>
        </main>

        {onSignOut && (
          // Sibling of `main`, not a child, so it stays pinned while `main` scrolls.
          // `pointer-events-none` on the container, `-auto` only on the button, so
          // clicks on the heading/rows beneath pass through. Visible `md:` up only.
          <div className="pointer-events-none absolute inset-x-0 top-0 z-10 hidden justify-start px-6 pt-6 md:flex">
            <Button
              type="button"
              variant="ghost"
              onClick={onSignOut}
              data-testid="workspace-selector-sign-out"
              // `sm`'s recipe (`button.styles.ts`) from `lg:` (1024px) up: a 40px
              // touch target is right on tablet, oversized once there's desktop
              // pointer precision and room either side of it.
              className="pointer-events-auto bg-surface-page text-text-secondary lg:h-8 lg:min-w-8 lg:gap-x-1 lg:rounded-md lg:px-2.5 lg:py-[9px] lg:text-sm lg:leading-8"
            >
              <LogOut aria-hidden className="rtl:-scale-x-100" />
              {intl.formatMessage(workspaceSelectorMessages.signOut)}
            </Button>
          </div>
        )}

        {onSignOut && (
          // Fades the list's last row into the sticky footer below rather than
          // a hard cut; the top-left overlay needs no such fade.
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-surface-page to-transparent md:hidden"
            aria-hidden
          />
        )}
      </div>

      {onSignOut && (
        // Flex sibling of the scroll wrapper, not an overlay: `main` shrinks to
        // leave room for it, pinning it to the bottom without `position: sticky`.
        // Phones only; tablet/desktop use the top-left corner button instead.
        <footer className="flex w-full justify-center p-6 md:hidden">
          <Button
            type="button"
            variant="ghost"
            onClick={onSignOut}
            data-testid="workspace-selector-sign-out-mobile"
            className="w-full max-w-xl text-text-secondary"
          >
            <LogOut aria-hidden className="rtl:-scale-x-100" />
            {intl.formatMessage(workspaceSelectorMessages.signOut)}
          </Button>
        </footer>
      )}
    </div>
  );
};

const InvitationRow = ({
  invitation,
  onJoin,
  status,
}: {
  invitation: WorkspaceInvitationView;
  onJoin: (invitation: WorkspaceInvitationView) => void;
  status: InvitationJoinState['status'] | null;
}) => {
  const intl = useIntl();
  const isJoining = status === 'joining';

  return (
    <li
      className="flex flex-col gap-2 rounded-xl border border-outline p-4"
      data-testid={`workspace-selector-invite-${invitation.invitation_id}`}
    >
      <div className="flex items-center gap-4">
        <WorkspaceAvatar
          id={invitation.workspace_id ?? invitation.team_id}
          name={invitation.workspace_name}
          iconUrl={invitation.icon_url}
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <TypographyP2Bold className="truncate text-text-primary">
            {invitation.workspace_name}
          </TypographyP2Bold>
          <TypographyCaption className="truncate text-text-secondary">
            {intl.formatMessage(workspaceSelectorMessages.invitationCaption)}
          </TypographyCaption>
        </span>
        <Button
          type="button"
          size="sm"
          disabled={isJoining}
          onClick={() => onJoin(invitation)}
          data-testid={`workspace-selector-join-${invitation.invitation_id}`}
        >
          {intl.formatMessage(
            isJoining
              ? workspaceSelectorMessages.joining
              : workspaceSelectorMessages.joinTeam
          )}
        </Button>
      </div>
      {status === 'failed' && (
        <TypographyCaption role="alert" className="text-validation-destructive">
          {intl.formatMessage(workspaceSelectorMessages.joinFailed)}
        </TypographyCaption>
      )}
    </li>
  );
};
